import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Table,
  message,
  Tag,
  Avatar,
  Button,
  Descriptions,
  DatePicker,
  Input,
} from "antd";
import { CaretDownOutlined, SyncOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { ApiManager, Locale, Util } from "../../utils";
import Base from "../Base";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();
const util = Util.getInstance();
const base = Base.getInstance();

function SpecialShareTransfers({ user }) {
  const thisSorter = useRef();
  const thisFilters = useRef();
  const [list, setList] = useState({ data: [], current_page: 1 });
  const [range, setRange] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const fetchList = useCallback(
    async (page = 1) => {
      const { token } = user;
      if (!token) {
        return;
      }
      setLoading(true);
      try {
        const apiUrl = api.specialShare.list;
        const apiConfig = {
          token,
          query: {
            page,
            keyword,
            count: 10,
          },
        };

        if (thisSorter.current && thisSorter.current.field) {
          const order = thisSorter.current.order.split("end")[0];
          apiConfig.query[`filter[sort]`] = `${thisSorter.current.field}@${order}`;
        }
        if (thisFilters.current) {
          Object.keys(thisFilters.current).forEach((key) => {
            if (thisFilters.current[key]) {
              apiConfig.query[`filter[${key}]`] = thisFilters.current[key].join(",");
            }
          });
        }
        if (range && range.length > 0) {
          apiConfig.query["filter[start_date]"] = range[0].format("YYYY-MM-DD");
          apiConfig.query["filter[end_date]"] = range[1].format("YYYY-MM-DD");
        }

        const res = await apiUrl.run(apiConfig);
        if (res.result) {
          setList(res.data);
        } else {
          message.warning(res.message);
        }
      } catch (error) {
        message.error(error.message);
      } finally {
        setLoading(false);
      }
    },
    [user, range, keyword]
  );

  const onSync = async (id) => {
    const { token } = user;
    if (!token) {
      return;
    }
    setSyncingId(id);
    try {
      const res = await api.specialShare.sync.run({
        token,
        path: { id },
      });
      if (res.result) {
        message.success(i18n.t("specialShareList.syncSuccess"));
        fetchList(list.current_page);
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      base.handleApiError(api.specialShare.sync, error);
    } finally {
      setSyncingId(null);
    }
  };

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const statusColor = (status) => {
    switch (status) {
      case 5:
        return "green";
      case 3:
      case 4:
        return "blue";
      case 6:
        return "red";
      default:
        return "default";
    }
  };

  const statusOptions = list.status
    ? Object.keys(list.status).map((key) => ({
        text: list.status[key],
        value: key,
      }))
    : [];

  return (
    <>
      <Input.Group compact style={{ marginBottom: 16 }}>
        <DatePicker.RangePicker
          defaultValue={range}
          format="YYYY-MM-DD"
          allowClear
          onChange={(dates) => setRange(dates)}
          suffixIcon={<CaretDownOutlined />}
        />
        <Input.Search
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={() => fetchList()}
          placeholder={i18n.t("specialShareList.searchPlaceholder")}
          style={{ width: 320 }}
          enterButton
          allowClear
        />
      </Input.Group>
      <Table
        expandable={{
          expandedRowRender: (record) => {
            if (!record.order) {
              return null;
            }
            return (
              <Descriptions title={i18n.t("financeOrderDetail.headerTitle")}>
                <Descriptions.Item label={i18n.t("commissionList.receiver")}>
                  {record.order.receiver}
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("commissionList.order_no")}>
                  {record.order.order_no}
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("commissionList.order_id")}>
                  {record.order.id}
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("commissionList.tel")}>
                  {record.order.tel}
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("commissionList.original")}>
                  {util.getFormattedPrice(record.order.original_price, "¥")}
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("commissionList.price")}>
                  {util.getFormattedPrice(record.order.price, "¥")}
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("commissionList.ship")}>
                  {util.getFormattedPrice(record.order.ship_price, "¥")}
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("commissionList.real")}>
                  {util.getFormattedPrice(record.order.real_payment, "¥")}
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("commissionList.address")}>
                  {record.order.address}
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("specialShareList.billNo")}>
                  {record.out_bill_no}
                </Descriptions.Item>
                <Descriptions.Item label={i18n.t("specialShareList.remark")}>
                  {record.transfer_remark}
                </Descriptions.Item>
                {record.fail_reason ? (
                  <Descriptions.Item label={i18n.t("specialShareList.failReason")}>
                    {record.fail_reason}
                  </Descriptions.Item>
                ) : null}
              </Descriptions>
            );
          },
        }}
        loading={loading}
        dataSource={list.data}
        rowKey={(record) => `${record.id}`}
        onChange={(pagination, filters, sorter) => {
          thisSorter.current = sorter;
          thisFilters.current = filters;
          fetchList(pagination.current);
        }}
        pagination={{
          current: list.current_page,
          total: list.total,
          pageSize: 10,
          showSizeChanger: false,
          showQuickJumper: true,
        }}
        columns={[
          {
            title: "id",
            key: "id",
            dataIndex: "id",
          },
          {
            title: i18n.t("users.avatar"),
            key: "avatar",
            render: (text, record) => (
              <Avatar
                size="large"
                shape="circle"
                src={record.partner?.avatar}
                alt={record.partner?.nickname}
              />
            ),
          },
          {
            title: i18n.t("users.name"),
            key: "nickname",
            render: (text, record) => record.partner?.nickname || "—",
          },
          {
            title: i18n.t("commissionList.mobile"),
            key: "mobile",
            align: "center",
            render: (text, record) => record.partner?.mobile || "—",
          },
          {
            title: i18n.t("commissionList.order_id"),
            key: "order_id",
            dataIndex: "order_id",
            align: "center",
          },
          {
            title: i18n.t("specialShareList.amount"),
            key: "transfer_amount",
            dataIndex: "transfer_amount",
            sorter: true,
            render: (text, record) =>
              util.getFormattedPrice(record.transfer_amount, "¥"),
          },
          {
            title: i18n.t("commissionList.status"),
            key: "status",
            align: "center",
            filters: statusOptions,
            render: (text, record) => (
              <Tag color={statusColor(record.status)}>{record.status_label}</Tag>
            ),
          },
          {
            title: i18n.t("users.date"),
            key: "created_at",
            dataIndex: "created_at",
            align: "center",
            sorter: true,
          },
          {
            title: i18n.t("users.actions"),
            key: "actions",
            align: "center",
            render: (text, record) => (
              <Button
                type="link"
                icon={<SyncOutlined />}
                loading={syncingId === record.id}
                onClick={() => onSync(record.id)}
              >
                {i18n.t("specialShareList.sync")}
              </Button>
            ),
          },
        ]}
      />
    </>
  );
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(SpecialShareTransfers);
